import { WaterCharacteristicsPreference } from "@src/core/api/waterCharacteristicsPreferences";
import { isNotNil } from "@src/shared/helpers/guards";
import { assert } from "radashi";
import { engine } from "..";
import { trackLiveObjects } from "../helpers/trackLiveObjects";
import { withDelete } from "../helpers/withDelete";
import { waterCharacteristicsRepository } from "../technicalParameters/waterCharacteristics/waterCharacteristicsRepository";
import {
  buildValidationResult,
  hasValidationResult,
  ValidationResult,
} from "../validationResults/buildValidationResult";
import { toArray } from "../vector";

export const utilsRepository = trackLiveObjects("utilsRepository", {
  convertPowerToFlow(
    power: number,
    differentialTemperature: number,
    userWaterCharacteristics: WaterCharacteristicsPreference | null,
  ): [null, number] | [ValidationResult[], null] {
    const utils = new engine.Utils();
    const technicalParameters = new engine.TechnicalParameters();
    const waterCharacteristics =
      technicalParameters.getWaterCharacteristicsParams();
    assert(
      waterCharacteristics,
      "waterCharacteristics is null in utilsRepository.convertPowerToFlow",
    );
    if (userWaterCharacteristics) {
      waterCharacteristicsRepository.init(
        waterCharacteristics,
        userWaterCharacteristics,
      );
    }

    const waterCharacteristicsErrors = toArray(
      waterCharacteristics.getErrorFields(),
    )
      .map(buildValidationResult)
      .filter(isNotNil);
    if (waterCharacteristicsErrors.length > 0) {
      return withDelete(technicalParameters, [
        waterCharacteristicsErrors,
        null,
      ]);
    }

    const result = utils.convertPowerToFlow(
      power,
      differentialTemperature,
      waterCharacteristics,
    );
    assert(result, "Result is null in utilsRepository.convertPowerToFlow");
    const validationResult = result.getValidationResult();

    if (hasValidationResult(validationResult)) {
      const builtValidationResult = buildValidationResult(validationResult);
      assert(
        builtValidationResult,
        "builtValidationResult is null in utilsRepository.convertPowerToFlow",
      );
      return withDelete(
        [technicalParameters, utils],
        [[builtValidationResult], null],
      );
    }

    return withDelete([technicalParameters, utils], [null, result.value]);
  },
  convertUnit(value: number, fromUnit: string, toUnit: string) {
    const utils = new engine.Utils();
    return withDelete(utils, utils.convertUnit(value, fromUnit, toUnit));
  },
  getUnitSymbol(unit: string) {
    const utils = new engine.Utils();
    return withDelete(utils, utils.getUnitSymbol(unit));
  },
  roundNumber(value: number, decimals: number) {
    const utils = new engine.Utils();
    return withDelete(utils, utils.roundNumber(value, decimals));
  },
});
